import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PersonalShell, { usePersonalProse } from "./PersonalShell";
import { SUPPORT_EMAIL } from "./DeleteAccountPage";
import { FOCUS } from "@/pages/home/ui";
import { FAMILY_FACE_DATA_POLICY_PATH } from "@/content/faceDataPolicy";

// /personal/support: the Photohawk Family app's support page, the App Store listing's support URL (photohawk-personal
// fastlane support_url.txt, and LegalLinks.support in the app). Public in production from 20 October 2026: contact
// details (support@photohawk.com and the company) and the questions App Review and people ask most.

export const SUPPORT_PATH = "/personal/support";

/** The FAQ entries, in order; "link" points at the page that says more. */
export const SUPPORT_FAQ: { key: string; link?: string }[] = [
  { key: "subscription" },
  { key: "refund" },
  { key: "restore" },
  { key: "delete", link: "/personal/delete-account" },
  { key: "faces", link: FAMILY_FACE_DATA_POLICY_PATH },
  { key: "photos" },
  { key: "prints" },
];

const SupportPage = () => {
  const { t } = useTranslation();
  const s = (key: string) => t(`home.support.${key}`);
  const prose = usePersonalProse();
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(s("emailSubject"))}`;
  return (
    <PersonalShell title={`${s("title")} | Photohawk`}>
      <article className={prose} data-testid="support">
        <h1>{s("title")}</h1>
        <p>{s("intro")}</p>

        <h2 id="contact">{s("contactHeading")}</h2>
        <p>{s("contactText")}</p>
        <p>
          <a
            href={mailto}
            data-testid="support-email"
            className={`!inline-flex h-11 items-center rounded-xl bg-primary px-6 text-[15px] !font-semibold !text-primary-foreground !no-underline shadow-md transition-all hover:bg-primary/90 hover:shadow-lg ${FOCUS}`}
          >
            {s("emailButton")}
          </a>
        </p>
        <p>
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>
        <p className="text-sm">{s("company")}</p>

        <h2 id="faq">{s("faqHeading")}</h2>
        {SUPPORT_FAQ.map(({ key, link }) => (
          <section key={key} id={key} data-testid={`support-faq-${key}`}>
            <h3>{s(`faq.${key}.q`)}</h3>
            <p>
              {s(`faq.${key}.a`)}
              {link && (
                <>
                  {" "}
                  <Link to={link}>{s(`faq.${key}.link`)}</Link>
                </>
              )}
            </p>
          </section>
        ))}

        <h2>{s("legalHeading")}</h2>
        <ul>
          <li>
            <Link to="/personal/privacy">{s("privacy")}</Link>
          </li>
          <li>
            <Link to="/personal/terms">{s("terms")}</Link>
          </li>
          <li>
            <Link to={FAMILY_FACE_DATA_POLICY_PATH}>{s("faceData")}</Link>
          </li>
        </ul>
      </article>
    </PersonalShell>
  );
};

export default SupportPage;
